import { CargoTypeMode, cargoTypeModeProxyNullHandler } from 'harmony-constants';
import { L } from 'harmony-language';
import React, { useState } from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { Medication, Stop } from '../../../../types';
import { useDraftsLoadsContext } from '../../drafts-loads-context';
import { PerformanceExpander } from '../../drafts-loads-table-cells/performance-expander';
import { DisplayStopCargoType } from '../../drafts-loads-table-cells/stops/display/display-stop-cargo-type';
import { DisplayStopCompartments } from '../../drafts-loads-table-cells/stops/display/display-stop-compartments';
import { DisplayStopLocation } from '../../drafts-loads-table-cells/stops/display/display-stop-location';
import { DisplayStopNotes } from '../../drafts-loads-table-cells/stops/display/display-stop-notes';
import { DisplayStopQuantity } from '../../drafts-loads-table-cells/stops/display/display-stop-quantity';
import { DisplayStopSublocations } from '../../drafts-loads-table-cells/stops/display/display-stop-sublocations';
import { DisplayStopText } from '../../drafts-loads-table-cells/stops/display/display-stop-text';
import { DisplayStopTime } from '../../drafts-loads-table-cells/stops/display/display-stop-time';
import { DisplayStopType } from '../../drafts-loads-table-cells/stops/display/display-stop-type';
import { DisplayStopWeight } from '../../drafts-loads-table-cells/stops/display/display-stop-weight';
import { DisplayUnitSwitcher } from '../../drafts-loads-table-cells/stops/unit-switcher';
import { getMedicationsTagStyle, stopGridTemplateColumnsGenerator } from '../../utils/drafts-loads-utils';
import { convertGramsToBushelsDisplay, getIsBushelCargo } from '../../../../utils/weight-utils';

interface DisplayStopTableRowProps {
    stop: Stop;
    trailerId: number | null; // compartments needs this passed down
}

export const DisplayStopTableRow: React.FC<DisplayStopTableRowProps> = (props) => {
    const { stop, trailerId } = props;
    const { isDrafts } = useDraftsLoadsContext();
    const { data: medications = [] } = useOrganizationQuery<Medication[]>(OrgQueryKeys.medications);
    const [secondRowVis, setSecondRowVis] = useState(false);
    const [unitState, setUnitState] = useState(cargoTypeModeProxyNullHandler(stop.cargoTypeId));
    const cargoMedsStyle = getMedicationsTagStyle(stop.medicationIds || [], medications);
    const medNames = stop.medicationIds?.map(id => medications.find(med => med.id === id)!.name);

    const handleUnitsChange = () => {
        setUnitState(prev => {
            return prev === CargoTypeMode.Quantity
                ? CargoTypeMode.Weight
                : prev === CargoTypeMode.Weight && getIsBushelCargo(stop)
                    ? CargoTypeMode.Bushels
                    : CargoTypeMode.Quantity;
        });
    }

    return (
        <>
            <div
                className='lt-row-stop lt-row-stop-item'
                style={{ gridTemplateColumns: stopGridTemplateColumnsGenerator(isDrafts, false) }}
            >
                <div className='lt-cell lt-cell-sub'><DisplayStopType stop={stop} /></div>
                <div className='lt-cell lt-cell-sub'><DisplayStopCargoType stop={stop} /></div>
                <div className='lt-cell lt-cell-sub'><DisplayStopLocation stop={stop} /></div>
                <div className='lt-cell lt-cell-sub'><DisplayStopSublocations stop={stop} /></div>

                <div className='lt-cell lt-cell-sub'><DisplayStopTime dateTime={stop.arrivalTime} /></div>
                {isDrafts ? null : <div className='lt-cell lt-cell-sub'><DisplayStopTime dateTime={stop.completedAt} /></div>}

                {unitState === CargoTypeMode.Weight ? (
                    <>
                        <div className='lt-cell lt-cell-sub'><DisplayStopWeight weight={stop.weight} /></div>
                        {isDrafts ? null : <div className='lt-cell lt-cell-sub'><DisplayStopWeight weight={stop.actualWeight} /></div>}
                    </>
                ) : ( unitState === CargoTypeMode.Bushels ? (
                    <>
                        <div className='lt-cell lt-cell-sub'>{stop.weight && stop.cargoTypeId ? convertGramsToBushelsDisplay(stop.weight, stop.cargoTypeId) : ''}</div>
                        {isDrafts ? null : <div className='lt-cell lt-cell-sub'>{stop.actualWeight && stop.cargoTypeId ? convertGramsToBushelsDisplay(stop.actualWeight, stop.cargoTypeId) : ''}</div>}
                    </>
                ) : ( // Default to quantity
                    <>
                        <div className='lt-cell lt-cell-sub'><DisplayStopQuantity quantity={stop.quantity} /></div>
                        {isDrafts ? null : <div className='lt-cell lt-cell-sub'><DisplayStopQuantity quantity={stop.actualQuantity} /></div>}
                    </>
                ))}

                <div className='lt-cell lt-cell-sub'>
                    <DisplayUnitSwitcher
                        stop={stop}
                        handleUnitsChange={handleUnitsChange}
                    />
                </div>

                <div className='lt-cell lt-cell-sub'><DisplayStopText text={stop.orderNumber} /></div>
                <div className='lt-cell lt-cell-sub'><DisplayStopText text={stop.productionPlan} /></div>

                <div className='lt-cell lt-cell-sub'><DisplayStopNotes stop={stop} /></div>

                <PerformanceExpander
                    title={stop.id?.toString() || ''} // is null for auto merged stops
                    expanded={secondRowVis}
                    onChange={() => {
                        setSecondRowVis(!secondRowVis);
                    }}
                />
            </div>
            {secondRowVis && <>
                <div className='lt-row-stop-hidden lt-row-stop-header'>
                    <div>{L.readyTime()}</div>
                    <div>{L.compartments()}</div>
                    <div>{L.lotId()}</div>
                    <div>{L.requiredBegin()}</div>
                    <div>{L.requiredEnd()}</div>
                    <div>{L.customerOrderNumber()}</div>
                    <div>{L.medication()}</div>
                </div>
                <div className='lt-row-stop-hidden'>
                    <div className='lt-cell lt-cell-sub'><DisplayStopTime dateTime={stop.readyTime} /></div>
                    <div className='lt-cell lt-cell-sub'><DisplayStopCompartments stop={stop} trailerId={trailerId} /></div>
                    <div className='lt-cell lt-cell-sub'><DisplayStopText text={stop.lotId} /></div>
                    <div className='lt-cell lt-cell-sub'><DisplayStopTime dateTime={stop.requiredBegin} /></div>
                    <div className='lt-cell lt-cell-sub'><DisplayStopTime dateTime={stop.requiredEnd} /></div>
                    <div className='lt-cell lt-cell-sub'><DisplayStopText text={stop.customerOrderNumber} /></div>
                    {stop.medicationIds && <div className='lt-cell lt-cell-sub' style={cargoMedsStyle}>{medNames?.join(', ')}</div>}
                </div>
            </>}
        </>
    );
};
